import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useMode } from '../../../../../../../hooks/useMode'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../ui-config'
import { Supplier, useAllSuppliers } from '../../../../../suppliers/data'
import {
  CompanyCode,
  CustomFieldDefinition,
  MasterDataSupplier,
} from '../../../data'
import { FormFieldName, FormValues } from '../common'
import { EditSupplierView } from './EditSupplier.view'

type Props = {
  handleSubmit: (formData: any, resetForm: any) => Promise<void>
  isRequestRunning: boolean
  handleClose: () => void
  supplier?: MasterDataSupplier
  customFieldsOptions?: CustomFieldDefinition[]
  companyCodesOptions?: CompanyCode[]
  allMasterDataSuppliers?: MasterDataSupplier[]
}

export const EditSupplierForm: React.FC<Props> = ({
  handleSubmit,
  isRequestRunning,
  handleClose,
  supplier,
  customFieldsOptions,
  companyCodesOptions,
  allMasterDataSuppliers,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const { data: suppliers } = useAllSuppliers()
  const mode = useMode()

  // Helper function to get the correct config based on mode
  const getModeConfig = () => {
    const masterDataConfig = config.selfservices.masterDataManagement.suppliers
    switch (mode) {
      case 'add':
        return masterDataConfig.add
      case 'info':
        return masterDataConfig.info
      case 'edit':
      default:
        return masterDataConfig.edit
    }
  }

  const modeConfig = getModeConfig()

  // Helper function to get initial company codes with default fallback
  const getInitialCompanyCodes = () => {
    const supplierCompanyCodes = supplier?.CompanyCodes || []

    // If no company codes exist and we have available options, add the default one
    if (supplierCompanyCodes.length === 0 && companyCodesOptions) {
      const defaultCompanyCode = companyCodesOptions.find(
        companyCode => companyCode.IsStandard,
      )
      if (defaultCompanyCode) {
        return [defaultCompanyCode]
      }
    }

    return supplierCompanyCodes
  }

  // Helper function to get available suppliers (filtering out already used ones)
  const getAvailableSuppliers = (): Supplier[] => {
    if (!suppliers) return []

    // Get all used supplier IDs from existing MasterDataSuppliers (excluding current one in edit mode)
    const usedSupplierIds = allMasterDataSuppliers
      ?.filter(masterSupplier =>
        mode === 'edit' ? masterSupplier.Id !== supplier?.Id : true,
      )
      ?.map(masterSupplier => masterSupplier.Common.DisplayId)
      .filter(id => id) // Remove empty/undefined IDs

    // Filter suppliers to show only unused ones
    const availableSuppliers = suppliers.filter(
      supplier => !usedSupplierIds?.includes(supplier.Id),
    )

    return availableSuppliers
  }

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      id: supplier?.Id || '',
      companySelection: undefined,
      displayId: supplier?.Common.DisplayId || '',
      company: supplier?.Common.Company || '',
      salesTaxID: supplier?.Common.SalesTaxID || '',
      taxNumber: supplier?.Common.TaxNumber || '',
      street: supplier?.Address.Street || '',
      postalCode: supplier?.Address.PostalCode || '',
      city: supplier?.Address.City || '',
      country: supplier?.Address.Country || 'Germany',
      customFields: supplier?.CustomFields || [],
      companyCodes: getInitialCompanyCodes(),
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'displayId',
          config: modeConfig.displayId,
        },
        {
          fieldName: 'company',
          config: modeConfig.company,
        },
        {
          fieldName: 'taxNumber',
          config: modeConfig.taxNumber,
        },
        {
          fieldName: 'salesTaxID',
          config: modeConfig.salesTaxID,
        },
        {
          fieldName: 'street',
          config: modeConfig.street,
        },
        {
          fieldName: 'postalCode',
          config: modeConfig.postalCode,
        },
        {
          fieldName: 'city',
          config: modeConfig.city,
        },
        {
          fieldName: 'country',
          config: modeConfig.country,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      // If the user did not select a supplier from the dropdown and
      // entered a displayId manually, we need to validate that
      if (!formData.companySelection) {
        if (!formData.displayId) {
          errors.displayId = t(
            'masterDataManagement.suppliers.table.dialog.form.billingInformation.id.validation.required',
          )
        } else if (formData.displayId && allMasterDataSuppliers) {
          const isDuplicateDisplayId = allMasterDataSuppliers.some(
            masterSupplier => {
              if (mode === 'edit' && masterSupplier.Id === formData.id) {
                return false
              }
              return masterSupplier.Common.DisplayId === formData.displayId
            },
          )

          if (isDuplicateDisplayId) {
            errors.displayId = t(
              'masterDataManagement.suppliers.table.dialog.form.billingInformation.id.validation.duplicate',
            )
          }

          // Check if displayId is available in dropdown but not selected from it
          if (!isDuplicateDisplayId && suppliers) {
            const isAvailableSupplierId = suppliers.find(
              s => s.Id === formData.displayId,
            )

            // In edit mode, allow the current supplier's displayId
            const isCurrentSupplierId =
              mode === 'edit' &&
              supplier?.Common.DisplayId === formData.displayId

            if (isAvailableSupplierId && !isCurrentSupplierId) {
              errors.displayId = t(
                'masterDataManagement.suppliers.table.dialog.form.billingInformation.id.validation.selectFromDropdown',
              )
            }
          }
        }

        // Company validation: required only
        if (!formData.company) {
          errors.company = t(
            'masterDataManagement.suppliers.table.dialog.form.billingInformation.company.validation.required',
          )
        }
      }

      // Company codes
      if (formData.companyCodes.length === 0) {
        errors.companyCodes = t(
          'masterDataManagement.suppliers.table.dialog.form.assignedCompanyCodes.table.validation.required',
        )
      }
      formData.companyCodes?.forEach((companyCode, index) => {
        const fieldErrors: any = {}

        if (!companyCode.Common.Company) {
          fieldErrors.Common = {
            Company: t(
              'masterDataManagement.suppliers.table.dialog.form.assignedCompanyCodes.table.columns.company.validation.required',
            ),
          }
        }

        // Only initialize and set errors if there are any
        if (Object.keys(fieldErrors).length > 0) {
          if (!errors.companyCodes) {
            errors.companyCodes = []
          }
          ;(errors.companyCodes as any)[index] = fieldErrors
        }
      })

      // Custom fields
      formData.customFields?.forEach((customField, index) => {
        const fieldErrors: any = {}

        if (!customField.CustomFieldDefinition.Key) {
          fieldErrors.CustomFieldDefinition = {
            Key: t(
              'masterDataManagement.suppliers.table.dialog.form.additionalProperties.table.columns.name.validation.required',
            ),
          }
        }

        if (
          customField.Value === null ||
          customField.Value === undefined ||
          customField.Value === ''
        ) {
          fieldErrors.Value = t(
            'masterDataManagement.suppliers.table.dialog.form.additionalProperties.table.columns.value.validation.required',
          )
        }

        // Only initialize and set errors if there are any
        if (Object.keys(fieldErrors).length > 0) {
          if (!errors.customFields) {
            errors.customFields = []
          }
          ;(errors.customFields as any)[index] = fieldErrors
        }
      })

      return errors
    },
    onSubmit: async formData => {
      await handleSubmit(formData, formik.resetForm)
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    const error = formik.errors[name]

    const errorMessage =
      typeof error === 'string' ? error : JSON.stringify(error)
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{errorMessage}</small>
      )
    )
  }

  const getNestedFormErrorMessage = (index: number, fieldName: string) => {
    // Determine which array to check based on fieldName
    const arrayName = fieldName.startsWith('Common.')
      ? 'companyCodes'
      : 'customFields'

    // Get errors and touched for the determined array
    const arrayErrors = (formik.errors as any)[arrayName]
    const arrayTouched = (formik.touched as any)[arrayName]

    // Navigate through the nested field path
    const fieldPath = fieldName.split('.')
    let hasError = arrayErrors?.[index]
    let isTouched = arrayTouched?.[index]

    for (const pathSegment of fieldPath) {
      hasError = hasError?.[pathSegment]
      isTouched = isTouched?.[pathSegment]
    }

    // Return error message if both error and touched exist
    if (hasError && isTouched) {
      return <small className="p-error">{hasError}</small>
    }

    return false
  }

  const availableCustomFieldsOptions = customFieldsOptions?.filter(
    customField =>
      !formik.values.customFields.some(
        assignedCustomField => assignedCustomField.Id === customField.Id,
      ),
  )

  return (
    <EditSupplierView
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      getNestedFormErrorMessage={getNestedFormErrorMessage}
      config={modeConfig}
      handleClose={handleClose}
      isRequestRunning={isRequestRunning}
      availableCompanyCodesOptions={companyCodesOptions}
      availableCustomFieldsOptions={availableCustomFieldsOptions}
      getAvailableSuppliers={getAvailableSuppliers}
    />
  )
}
